import { FC, ReactNode } from 'react'
import Stack from 'react-bootstrap/Stack'
import { useIntl } from 'react-intl'

import { ID } from '@models/base'
import { QueueNodeModel } from '@models/queueNode'

import { Button } from '@components/Button'
import { Tooltip } from '@components/Tooltip'

import { PermissionsControl } from '@modules/permissions'
import { QueueNodeRedeployButton } from '@modules/queues/components/QueueNodeRedeployButton'

import { PermissionEnum } from '@/constants/permission'
import TypeUtil from '@/utils/typeUtil'

import { QueueNodeCallButton } from '../QueueNodeCallButton'
import { QueueNodePatientNotes } from '../QueueNodePatientNotes'
import QueueNodePatientVisitType from '../QueueNodePatientVisitType/QueueNodePatientVisitType'
import { QueueNodePreferredCounterBadge } from '../QueueNodePreferredCounterBadge'
import { QueueNodePriorityBadge } from '../QueueNodePriorityBadge/QueueNodePriorityBadge'
import { QueueNodeReprintButton } from '../QueueNodeReprintButton'

type Props = {
  node: QueueNodeModel
  queueId: ID
  draggableHandler?: ReactNode
  isRedeployable?: boolean
  isCompletable?: boolean
  isCallable?: boolean
  onEdit: (queueNode: QueueNodeModel) => void
  onComplete?: (queueNode: QueueNodeModel) => void
}

const QueueNodeBubble: FC<Props> = ({
  node,
  queueId,
  draggableHandler,
  isCompletable,
  isRedeployable,
  isCallable,
  onEdit,
  onComplete,
}) => {
  const intl = useIntl()

  const isHaveNotes =
    node.patient_visit_note ||
    node.patient_medical_note ||
    (TypeUtil.isDefined(node.patient_medicine_allergies) && !TypeUtil.isEmpty(node.patient_medicine_allergies))

  return (
    <div className="bg-body d-flex align-items-center rounded p-3 mb-2 border">
      {/* begin::queue-detail */}

      {/* begin::Draggable */}
      {draggableHandler}
      {/* end::Draggable */}

      <div className="me-3">{node.priority && <QueueNodePriorityBadge priority={node.priority} />}</div>

      <div className="text-truncated me-2">
        <Stack
          gap={2}
          direction="horizontal"
        >
          <div className="fs-4 fw-medium">{node.number}</div>
          <Stack
            gap={1}
            direction="horizontal"
          >
            {node.queue_counter_name && <QueueNodePreferredCounterBadge counterName={node.queue_counter_name} />}
            {node.patient_visit_type && <QueueNodePatientVisitType patientVisitType={node.patient_visit_type} />}
          </Stack>
        </Stack>
        <div className="fw-medium text-gray-700">
          <i className="fa-solid fa-user fs-9 me-2"></i>
          {node?.patient_name || node?.name || '-'}
        </div>
      </div>

      <Stack
        gap={2}
        direction="horizontal"
      >
        {isHaveNotes && (
          <QueueNodePatientNotes
            triageNotes={node.patient_visit_note}
            medicalNotes={node.patient_medical_note}
            medicineAllergies={node.patient_medicine_allergies}
          />
        )}

        {isRedeployable && (
          <PermissionsControl allow={[PermissionEnum.QUEUE_NUMBER_MOVE]}>
            <QueueNodeRedeployButton
              number={node.number}
              queueId={queueId}
              queueNumberId={node.queue_number_id}
            />
          </PermissionsControl>
        )}

        {isCompletable && (
          <PermissionsControl allow={[PermissionEnum.QUEUE_NUMBER_COMPLETE]}>
            <Button
              variant="icon"
              theme="secondary"
              size="sm"
              onClick={() => onComplete?.(node)}
            >
              <i className="fas fa-check my-2 py-2" />
            </Button>
          </PermissionsControl>
        )}

        {isCallable && (
          <PermissionsControl allow={[PermissionEnum.QUEUE_NUMBER_CALL]}>
            <QueueNodeCallButton queueNumberId={node.queue_number_id} />
          </PermissionsControl>
        )}

        <PermissionsControl allow={[PermissionEnum.QUEUE_NUMBER_REPRINT]}>
          <QueueNodeReprintButton queueNumber={node.number} />
        </PermissionsControl>

        <PermissionsControl allowIfAll={[PermissionEnum.QUEUE_NUMBER_UPDATE, PermissionEnum.QUEUE_NUMBER_GET]}>
          <Tooltip
            text={intl.formatMessage({ id: 'queue_number.edit' })}
            placement="top"
          >
            <Button
              variant="icon"
              theme="secondary"
              size="sm"
              onClick={() => onEdit(node)}
            >
              <i className="fas fa-pen my-2 py-2" />
            </Button>
          </Tooltip>
        </PermissionsControl>
      </Stack>
    </div>
  )
}

export { QueueNodeBubble }
